import { FC } from 'react';
import { clsx } from 'clsx';
import { Checkbox, FormControlLabel, FormGroup, Typography } from '@mui/material';
import { ReactComponent as FlagIcon } from '../../../../assets/icons/flag-icon-filled.svg';
import { useRecoilState } from 'recoil';
import { useTranslation } from 'react-i18next';

import './PriorityFilter.scss';
import { taskSidebarFiltersAtom } from '../../../../data-access';

export type PriorityOption = 'high' | 'medium' | 'low';

// Flag colors from pen file Priority options: High #f34720, Medium #fdc913, Low (gray)
const PRIORITY_OPTIONS: {
  value: PriorityOption;
  labelKey: string;
  flagColor: string;
}[] = [
  { value: 'high', labelKey: 'task.filters.priorities.high', flagColor: 'var(--color-red-base)' },
  {
    value: 'medium',
    labelKey: 'task.filters.priorities.medium',
    flagColor: 'var(--color-yellow-base)',
  },
  { value: 'low', labelKey: 'task.filters.priorities.low', flagColor: 'var(--color-gray-base)' },
];

const PriorityFilter: FC = () => {
  const { t } = useTranslation();
  const [filters, setFilters] = useRecoilState(taskSidebarFiltersAtom);

  const handlePriorityChange = (value: PriorityOption) => {
    setFilters((prev) => ({
      ...prev,
      priority: prev.priority === value ? null : value,
    }));
  };

  return (
    <div className="priority-filter">
      <Typography variant="body2" className="priority-filter__title">
        {t('task.filters.priority')}
      </Typography>
      <FormGroup className="priority-filter__options">
        {PRIORITY_OPTIONS.map((option) => {
          const isSelected = filters.priority === option.value;
          return (
            <FormControlLabel
              key={option.value}
              className={clsx('priority-filter__item', {
                'priority-filter__item--selected': isSelected,
              })}
              control={
                <Checkbox
                  size="small"
                  checked={isSelected}
                  onChange={() => handlePriorityChange(option.value)}
                />
              }
              label={
                <div className="priority-filter__item__content">
                  <FlagIcon
                    className="priority-filter__item__flag"
                    style={{ color: option.flagColor }}
                  />
                  <Typography variant="body2" className="priority-filter__item__text">
                    {t(option.labelKey)}
                  </Typography>
                </div>
              }
            />
          );
        })}
      </FormGroup>
    </div>
  );
};

export default PriorityFilter;
